import { Location, PlatformLocation } from "@angular/common";
import { Inject, Injectable } from "@angular/core";
import { NavigationExtras, Router, UrlHandlingStrategy, UrlSerializer } from "@angular/router";
import { FetchStatusType } from "@obg/ngrx-utils";
import { DocumentRef, StatusCodes } from "@obg/ngx.global";
import { LoggingFactory } from "@obg/ngx.global.evo";
import {
	LazyLoadingService,
	NavigateByCommandPayload,
	NavigateByUrlPayload,
	NavigatePayload,
	ROUTER_CONFIG,
	RouterConfig,
	RouteService,
} from "@obg/ngx.router";
import { REQUEST_TOKEN, RESPONSE_TOKEN } from "@obg/ngx.universal-express";
import { Request, Response } from "express";
import { firstValueFrom } from "rxjs";
import { map, tap } from "rxjs/operators";

@Injectable()
export class ServerRouteService extends RouteService {
	constructor(
		private _router: Router,
		private urlHandlingStrategy: UrlHandlingStrategy,
		private _location: Location,
		private urlSerializer: UrlSerializer,
		_documentRef: DocumentRef,
		_lazyLoadingService: LazyLoadingService,
		_platformLocation: PlatformLocation,
		loggingFactory: LoggingFactory,
		@Inject(ROUTER_CONFIG) config: RouterConfig,
		@Inject(RESPONSE_TOKEN) private response: Response,
		@Inject(REQUEST_TOKEN) private request: Request
	) {
		super(_documentRef, _router, _location, _lazyLoadingService, _platformLocation, config, loggingFactory);
	}

	navigateByCommand(payload: NavigateByCommandPayload): Promise<boolean> {
		const urlTree = this._router.createUrlTree(payload.commands, payload.extras).toString();
		const url = this._location.prepareExternalUrl(urlTree);

		if (payload.extras && payload.extras.skipLocationChange) {
			return this.redirect(urlTree, payload.extras);
		}

		return this.redirect(url, payload.extras);
	}

	navigateByRedirect(payload: NavigatePayload): Promise<FetchStatusType | undefined> {
		return firstValueFrom(
			this.getRouteConfig$(payload.routeName).pipe(
				tap(() => this.redirect(this.generateUrl(payload, true), payload.extras)),
				map((config) => config && FetchStatusType.Success)
			)
		);
	}

	navigateByUrl(payload: NavigateByUrlPayload): Promise<boolean> {
		return this.redirect(payload.url, payload.extras);
	}

	private redirect(url: string, extras?: NavigationExtras, code = StatusCodes.Found): Promise<boolean> {
		const urlTree = this.urlSerializer.parse(url);
		let appUrl = urlTree.toString();
		appUrl = appUrl.length === 1 ? appUrl : Location.stripTrailingSlash(appUrl);
		const requestUrl = this.urlSerializer.parse(this.request.path).toString();

		if (appUrl === requestUrl || !this.urlHandlingStrategy.shouldProcessUrl(urlTree)) {
			return Promise.resolve(false);
		}

		if (extras?.skipLocationChange) {
			return this._router.navigateByUrl(url);
		}

		return new Promise((resolve, reject) => {
			try {
				this.response.redirect(code, url);
				resolve(true);
			} catch (err) {
				reject(err);
			}
		});
	}
}
